/**
 * Shared aircraft table display components
 * Used in History.js and Airport.js for consistent aircraft display
 */

import React, { useState } from 'react';
import { getAirlineLogo, hasAirlineLogo, useAirlineLogoMapVersion } from '../utils/airlineLogoMap';
import { getMilitaryLogo } from '../utils/militaryIconCache';
import { getAircraftSpritePosition, getAircraftSizeMultiplier } from '../utils/aircraftSpriteMap';
import spriteImage from '../assets/BelugaProject/beluga_sprites.png';
import { airlineCodeFromCallsign } from '../utils/callsignAirline';

/**
 * Country flag display using CSS sprite
 */
export const FlagIcon = ({ countryCode, country, className = '' }) => {
  if (!countryCode) return <span>-</span>;
  return (
    <span
      className={`flag flag-${countryCode.toLowerCase()} ${className}`}
      title={country || countryCode.toUpperCase()}
    />
  );
};

/**
 * Airline logo display with fallback to text
 */
export const AirlineLogo = ({
  airlineCode,
  airlineName,
  className = 'airline-logo',
  showTextFallback = true
}) => {
  const [hasError, setHasError] = useState(false);
  // Re-render when the backend logo map arrives / changes.
  useAirlineLogoMapVersion();

  if (!airlineCode || !hasAirlineLogo(airlineCode) || hasError) {
    if (!showTextFallback) return null;
    return <span className="airline-text" title={airlineName}>{airlineCode || '-'}</span>;
  }

  const logoPath = getAirlineLogo(airlineCode, 'icon');

  return (
    <img
      src={logoPath}
      alt={airlineCode}
      className={className}
      title={airlineName || airlineCode}
      loading="lazy"
      onError={() => setHasError(true)}
    />
  );
};

/**
 * Military roundel with fallback to country flag.
 * The path comes from the icon cache, which lowercases the code (the backend
 * sends ISO "GB"; S3 keys are case-sensitive, so /military/GB/ is a 404) and
 * picks a service-specific roundel when the operator name names one.
 */
export const MilitaryIcon = ({ countryCode, country, operatorName = null }) => {
  const [hasError, setHasError] = useState(false);
  const iconPath = countryCode ? getMilitaryLogo(countryCode, operatorName) : null;

  if (!iconPath || hasError) {
    if (countryCode) {
      return <span className={`flag flag-${countryCode.toLowerCase()}`} title={`Military ${country}`}></span>;
    }
    return <span title="Military">MIL</span>;
  }

  return (
    <img
      src={iconPath}
      alt={`Military ${countryCode.toUpperCase()}`}
      className="military-icon"
      title={`Military ${country || countryCode.toUpperCase()}`}
      loading="lazy"
      onError={() => setHasError(true)}
    />
  );
};

/**
 * Aircraft type icon using sprite sheet
 */
export const AircraftTypeIcon = ({ aircraftType, size = 12 }) => {
  if (!aircraftType) return null;

  const spritePos = getAircraftSpritePosition(aircraftType);
  const sizeMultiplier = getAircraftSizeMultiplier(aircraftType);
  const iconSize = Math.round(size * sizeMultiplier);
  const bgPosX = -(spritePos.col * iconSize);
  const bgPosY = -(spritePos.row * iconSize);

  const iconStyle = {
    display: 'inline-block',
    width: `${iconSize}px`,
    height: `${iconSize}px`,
    backgroundImage: `url(${spriteImage})`,
    backgroundSize: `${iconSize * 8}px auto`,
    backgroundPosition: `${bgPosX}px ${bgPosY}px`,
    backgroundRepeat: 'no-repeat',
    filter: 'brightness(0) saturate(100%) brightness(0.3)',
    verticalAlign: 'middle',
    transform: 'rotate(90deg)',
    marginLeft: '3px'
  };

  return <span style={iconStyle} title={aircraftType}></span>;
};

/**
 * Aircraft type display with code and icon
 */
export const AircraftTypeDisplay = ({ aircraftType, iconSize = 12 }) => {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }} title={aircraftType}>
      <span>{aircraftType || '-'}</span>
      {aircraftType && <AircraftTypeIcon aircraftType={aircraftType} size={iconSize} />}
    </span>
  );
};

/**
 * Ground-vehicle (truck) icon — the one silhouette used by the live table, the
 * map marker and History for airport surface vehicles.
 */
export const GROUND_VEHICLE_SVG_PATHS = `<rect x="1" y="10" width="15" height="7" rx="1"/>
<path d="M16 10V7a1 1 0 0 1 1-1h2.6a1 1 0 0 1 .7.3l1.4 1.4a1 1 0 0 1 .3.7V16a1 1 0 0 1-1 1h-1"/>
<circle cx="6" cy="17" r="2" fill="currentColor"/>
<circle cx="18" cy="17" r="2" fill="currentColor"/>
<line x1="16" y1="10" x2="16" y2="17"/>`;

export const GroundVehicleIcon = ({ size = 14, title = 'Ground vehicle' }) => (
  <svg
    className="ground-vehicle-icon"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <title>{title}</title>
    <rect x="1" y="10" width="15" height="7" rx="1" />
    <path d="M16 10V7a1 1 0 0 1 1-1h2.6a1 1 0 0 1 .7.3l1.4 1.4a1 1 0 0 1 .3.7V16a1 1 0 0 1-1 1h-1" />
    <circle cx="6" cy="17" r="2" fill="currentColor" />
    <circle cx="18" cy="17" r="2" fill="currentColor" />
    <line x1="16" y1="10" x2="16" y2="17" />
  </svg>
);

/**
 * Status badge (NEW, REGULAR, MIL, VEHICLE)
 */
export const StatusBadge = ({ type, children }) => {
  const typeClass = type.toLowerCase();
  return <span className={`status-badge ${typeClass}`}>{children || type}</span>;
};

/**
 * Status indicators row (shows NEW, MIL, REGULAR badges as applicable)
 */
export const StatusIndicators = ({ isNew, isRegular, military, groundVehicle }) => {
  const badges = [];
  if (groundVehicle) badges.push(<StatusBadge key="vehicle" type="vehicle">VEHICLE</StatusBadge>);
  if (isNew) badges.push(<StatusBadge key="new" type="new">NEW</StatusBadge>);
  if (military) badges.push(<StatusBadge key="mil" type="mil">MIL</StatusBadge>);
  if (isRegular && !isNew) badges.push(<StatusBadge key="regular" type="regular">REGULAR</StatusBadge>);

  if (badges.length === 0) return null;
  return <span className="status-indicators">{badges}</span>;
};

/**
 * Operator cell display - airline logo for civilian, military icon for military
 */
export const OperatorCell = ({
  military,
  countryCode,
  country,
  airlineCode,
  airlineName,
  callsign,
  registration,
  showName = false
}) => {
  // Extract airline code from callsign if not provided (never for military,
  // never when the callsign is the registration)
  let effectiveAirlineCode = airlineCode;
  if (!effectiveAirlineCode && callsign) {
    const extractedCode = airlineCodeFromCallsign(callsign, { registration, military });
    if (extractedCode && hasAirlineLogo(extractedCode)) {
      effectiveAirlineCode = extractedCode;
    }
  }

  if (military) {
    // For military aircraft, show military icon with operator name
    const operatorName = airlineName || 'Military';
    return (
      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        {countryCode && <MilitaryIcon countryCode={countryCode} country={country} operatorName={airlineName} />}
        {showName && <span className="airline-text" title={operatorName}>{operatorName}</span>}
      </span>
    );
  }

  // For civilian aircraft, show airline logo
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      <AirlineLogo airlineCode={effectiveAirlineCode} airlineName={airlineName} />
      {showName && airlineName && <span className="airline-text" title={airlineName}>{airlineName}</span>}
    </span>
  );
};

/**
 * Format time for display (HH:MM:SS or HH:MM)
 */
export const formatTime = (dateTime, includeSeconds = false) => {
  if (!dateTime) return '-';
  try {
    const date = new Date(dateTime);
    if (isNaN(date.getTime())) return '-';
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    if (includeSeconds) {
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${hours}:${minutes}:${seconds}`;
    }
    return `${hours}:${minutes}`;
  } catch (e) {
    return '-';
  }
};

/**
 * Format duration in minutes to human-readable string
 */
/**
 * Elapsed time on the ground, at the precision that is still meaningful: hours and minutes
 * up to 6 h (a turnaround), whole hours from 6 h to 48 h (an overnight stay), whole days
 * beyond that (long-term parking, storage). Always rounds down, like a stopwatch.
 */
export const formatDurationMinutes = (minutes) => {
  if (minutes === null || minutes === undefined) return '-';
  const m = Math.max(0, Math.floor(minutes));
  if (m < 60) return `${m}m`;
  if (m < 6 * 60) return `${Math.floor(m / 60)}h ${m % 60}m`;
  if (m < 48 * 60) return `${Math.floor(m / 60)}h`;
  return `${Math.floor(m / 1440)}d`;
};

/**
 * Extract airline code from callsign. Pass the registration / military flag
 * when known so a registration flown as callsign is not mistaken for an airline.
 */
export const extractAirlineCode = (callsign, context) => airlineCodeFromCallsign(callsign, context);

const AircraftTableComponents = {
  FlagIcon,
  AirlineLogo,
  MilitaryIcon,
  AircraftTypeIcon,
  AircraftTypeDisplay,
  StatusBadge,
  StatusIndicators,
  OperatorCell,
  formatTime,
  formatDurationMinutes,
  extractAirlineCode
};

export default AircraftTableComponents;
